import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import {
    Mail,
    MapPin,
    MessageCircle,
    Phone
} from 'lucide-react'
import Container from '@/components/landing/container'
import EnquiryForm from '@/components/landing/enquiry-form'
import SectionHeading from '@/components/landing/section-heading'

import { CONTACT, LINKS } from '@/constants/landing'

type ContactItem = {
    label: string
    value: ReactNode
    href: string
    icon: LucideIcon
    external?: boolean
}

const CONTACT_ITEMS: ContactItem[] = [
    { label: "Call us", value: CONTACT.phoneDisplay, href: CONTACT.phoneHref, icon: Phone },
    { label: "WhatsApp", value: CONTACT.whatsappDisplay, href: LINKS.whatsappLearner, icon: MessageCircle, external: true },
    { label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, icon: Mail },
    { label: "Visit us", value: CONTACT.addressLines.join(', '), href: CONTACT.mapsHref, icon: MapPin, external: true }
]

const ContactSection: FC = (): ReactNode => {
    return (
        <section id='contact' aria-labelledby='contact-title' className='relative isolate overflow-hidden py-20 sm:py-28'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                <div className='absolute -left-40 top-20 size-[32rem] rounded-full bg-brand-teal/10 blur-3xl' />
                <div className='absolute -bottom-40 right-0 size-[28rem] rounded-full bg-brand-orange/10 blur-3xl' />
            </div>

            <Container className='grid gap-12 lg:grid-cols-12 lg:gap-16'>
                <div className='lg:col-span-5'>
                    <SectionHeading
                        id='contact-title'
                        eyebrow="Get in touch"
                        title="Ready to get hired, or to hire?"
                        description="Leave your details and our team will call you back. Prefer to talk now? Call or message us directly."
                    />

                    <ul className='mt-10 space-y-3'>
                        {CONTACT_ITEMS.map(item => {
                            const Icon = item.icon

                            return (
                                <li key={item.label}>
                                    <a
                                        href={item.href}
                                        target={item.external ? '_blank' : undefined}
                                        rel={item.external ? 'noopener noreferrer' : undefined}
                                        className='group flex items-center gap-4 rounded-2xl border border-border bg-card p-4 transition-colors hover:border-primary/30'
                                    >
                                        <span className='grid size-12 shrink-0 place-items-center rounded-xl bg-secondary text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground'>
                                            <Icon className='size-5' aria-hidden='true' />
                                        </span>

                                        <span className='min-w-0'>
                                            <span className='block text-sm text-muted-foreground'>
                                                {item.label}
                                            </span>

                                            <span className='block break-words font-semibold text-foreground'>
                                                {item.value}
                                            </span>
                                        </span>

                                        {item.external && <span className='sr-only'>(opens in a new tab)</span>}
                                    </a>
                                </li>
                            )
                        })}
                    </ul>
                </div>

                <div className='lg:col-span-7'>
                    <div className='rounded-[2rem] border border-border bg-card p-6 shadow-[0_40px_90px_-50px_rgba(16,22,90,0.45)] sm:p-10'>
                        <EnquiryForm />
                    </div>
                </div>
            </Container>
        </section>
    )
}

export default ContactSection
